import { Link } from "react-router-dom";
import { ArrowUpRight, Globe2, ShieldCheck, Smartphone, Zap } from "lucide-react";
import { useLanguage } from "../i18n";
import { usePageMeta } from "../hooks/usePageMeta";
import { Reveal } from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const HERO_IMG = "https://images.unsplash.com/photo-1711535767116-6bc0d7948fb9?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function HowItWorks() {
  usePageMeta("seo.howTitle", "seo.howDesc");
  const { t } = useLanguage();

  return (
    <div data-testid="how-it-works-page" className="px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto py-14 sm:py-20">
      <Reveal>
        <div className="text-center max-w-2xl mx-auto">
          <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-4">{t("hero.eyebrow")}</div>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-slate-900 text-balance">{t("how2.heroTitle")}</h1>
          <p className="mt-5 text-lg text-slate-600">{t("how2.heroSub")}</p>
        </div>
        <div className="mt-12 rounded-[2.5rem] overflow-hidden shadow-2xl shadow-orange-200/60">
          <img src={HERO_IMG} alt="eSIM" className="w-full h-64 sm:h-80 object-cover" />
        </div>
      </Reveal>

      <Reveal className="mt-16">
        <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-8 sm:p-12 grid md:grid-cols-[auto_1fr] gap-8 items-center">
          <span className="w-16 h-16 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center">
            <Smartphone className="w-8 h-8" />
          </span>
          <div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mb-3">{t("how2.whatT")}</h2>
            <p className="text-slate-600 leading-relaxed">{t("how2.whatX")}</p>
          </div>
        </div>
      </Reveal>

      <div className="mt-16 grid md:grid-cols-3 gap-5">
        {[
          [<Globe2 key="1" className="w-6 h-6" />, t("how.s1t"), t("how.s1x")],
          [<ShieldCheck key="2" className="w-6 h-6" />, t("how.s2t"), t("how.s2x")],
          [<Zap key="3" className="w-6 h-6" />, t("how.s3t"), t("how.s3x")],
        ].map(([icon, title, text], i) => (
          <Reveal key={title} delay={i * 0.1}>
            <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-8 h-full hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-100 transition-all duration-200">
              <div className="flex items-center justify-between mb-8">
                <span className="w-12 h-12 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center">{icon}</span>
                <span className="font-mono-t text-xs font-bold text-slate-300">0{i + 1}</span>
              </div>
              <h3 className="font-display font-bold text-xl text-slate-900 mb-3">{title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-16">
        <div className="rounded-3xl bg-[#E6F7F5] p-8 sm:p-12">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mb-3">{t("faq.q4")}</h2>
          <p className="text-slate-600 leading-relaxed">{t("faq.a4")}</p>
          <Link to="/compatibility" data-testid="hiw-compat-hero-link"
            className="mt-4 inline-flex items-center gap-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-[#FF6B35] hover:underline">
            {t("hiw.compatLink")} <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </Reveal>

      <section data-testid="faq-accordion" className="mt-16">
        <Reveal>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900 mb-6">{t("faq.title")}</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <AccordionItem key={n} value={`faq-${n}`} className="rounded-2xl bg-white border border-slate-200/80 px-6 shadow-sm">
                <AccordionTrigger data-testid={`faq-question-${n}`} className="font-display font-semibold text-left text-slate-900 hover:no-underline">
                  {t(`faq.q${n}`)}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">
                  {t(`faq.a${n}`)}
                  {n === 4 && (
                    <Link to="/compatibility" data-testid="hiw-compat-link"
                      className="mt-3 inline-flex items-center gap-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-[#FF6B35] hover:underline">
                      {t("hiw.compatLink")} <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <Reveal className="mt-16 text-center">
        <Link to="/destinations" data-testid="how2-cta"
          className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] text-white px-10 py-4 font-mono-t text-xs font-bold uppercase tracking-[0.25em] hover:bg-[#E8551F] transition-colors shadow-lg shadow-orange-200">
          {t("cta.button")} <ArrowUpRight className="w-4 h-4" />
        </Link>
      </Reveal>
    </div>
  );
}
